import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/common/AppText';
import { Lock } from 'lucide-react-native';
import { useTheme } from '../../hooks/useTheme';
import { useLocale } from '../../hooks/useLocale';
import { useFont } from '../../providers/FontProvider';

type Field = {
  label: string;
  value?: string | null;
};

export function UaePassVerifiedFieldList({
  fields,
}: {
  fields: Field[];
}) {
  const { theme } = useTheme();
  const { fonts } = useFont();
  const { isRTL } = useLocale();
  const visibleFields = fields.filter((field) => Boolean(field.value));

  return (
    <View style={[styles.card, { borderColor: theme.border }]}>
      {visibleFields.map((field, index) => (
        <View key={field.label}>
          {index > 0 ? (
            <View style={[styles.divider, { backgroundColor: theme.border }]} />
          ) : null}
          <View style={[styles.row, isRTL ? styles.rowReverse : null]}>
            <View style={styles.rowContent}>
              <Text
                style={[
                  styles.label,
                  {
                    color: theme.textTertiary,
                    fontFamily: fonts.bold,
                    textAlign: isRTL ? 'right' : 'left',
                    writingDirection: isRTL ? 'rtl' : 'ltr',
                  },
                ]}
              >
                {field.label}
              </Text>
              <Text
                style={[
                  styles.value,
                  {
                    color: theme.text,
                    fontFamily: fonts.medium,
                    // SCRUM-311: verified values are user content (Arabic/English
                    // names, contact info). Bidi-auto so each aligns to its own
                    // script and punctuation stays attached, instead of being
                    // force-aligned to the locale edge.
                    textAlign: 'auto',
                    writingDirection: 'auto',
                  },
                ]}
              >
                {field.value}
              </Text>
            </View>
            <Lock color={theme.textTertiary} size={19} strokeWidth={1.75} />
          </View>
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 22,
    borderWidth: 1,
    overflow: 'hidden',
  },
  divider: {
    height: StyleSheet.hairlineWidth,
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 12,
    paddingHorizontal: 24,
    paddingVertical: 20,
  },
  rowReverse: {
    flexDirection: 'row-reverse',
  },
  rowContent: {
    flex: 1,
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 2,
    lineHeight: 20,
  },
  value: {
    fontSize: 18,
    lineHeight: 26,
    fontWeight: '500',
  },
});
